import React, { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Address, CollectionPoint, DeliveryFormData, OrderItem, Payment } from '../types';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import CreateDeliveryModal from '../components/CreateDeliveryModal';
import { useToast } from '../hooks/useToast';
import OrderOverviewTab   from '../components/Orders/OrderOverview';
import OrderDetailsTab    from '../components/Orders/OrderDetailsTab';
import PaymentsTab        from '../components/Orders/PaymentsTab';
import StatusesLogsTab    from '../components/Orders/StatusesLogsTab';
import ConfirmModal       from '../components/ConfirmModal.tsx';
import DeliveryDetailsTab from '../components/Orders/DeliveriesTab';
import { useOrderDetail } from '../hooks/useOrderDetail';
import { useTimeline }    from '../hooks/useTimeline';
import { useOrders }      from '../hooks/useOrders';
import { usePickers }     from '../hooks/usePickers';
import { usePayments }    from '../hooks/usePayments';
import { assignDriverToOrderInDb }     from '../services/deliveryService';
import { unassignPickerFromOrderInDb }  from '../services/unassignPickerService';
import { fetchAddressByIdFromDb }      from '../services/customerService';
import { fetchCollectionPointsFromDb } from '../services/collectionPointService';
import { supabase } from '../clients/supabaseClient';
import { ORDER_STATUS, ARCHIVED_ORDER_STATUSES, type OrderStatus } from '../constants/orderStatuses';
import {
  canShowPrintButton, canAssignPacker, canUnassignPicker,
  canShowDeliveryButton, canShowCompleteCollectionButton,
  getCompleteCollectionLabel, showFulfillmentActions as canShowFulfillmentActions,
  getPaymentStageLabel,
} from '../domain/orderWorkflow';

type TabType = 'overview' | 'details' | 'payments' | 'deliveries' | 'logs';

const OrderDetails: FC = () => {
  const { success, error } = useToast();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const { order: currentOrder, items: orderItems, itemHistory, walletTransactions, cakeOrder, loading, fetchOrder, fetchDeliveries } = useOrderDetail();
  const { payments, paymentHistory, fetchPayments } = usePayments();
  const { timeline, loading: timelineLoading, fetchTimeline } = useTimeline();
  const { updateOrderStatus } = useOrders();
  const { pickers, fetchPickers } = usePickers();

  const [activeTab,          setActiveTab]          = useState<TabType>('overview');
  const [paymentsLoaded,     setPaymentsLoaded]     = useState(false);
  const [deliveriesLoaded,   setDeliveriesLoaded]   = useState(false);
  const [selectedPickerId,   setSelectedPickerId]   = useState('');
  const [showPackingConfirm,       setShowPackingConfirm]       = useState(false);
  const [showUnassignPickerConfirm, setShowUnassignPickerConfirm] = useState(false);
  const [showPrintConfirm,   setShowPrintConfirm]   = useState(false);
  const [showPinModal,       setShowPinModal]       = useState(false);
  const [enteredPin,         setEnteredPin]         = useState('');
  const [pinError,           setPinError]           = useState('');
  const [isModalOpen,        setIsModalOpen]        = useState(false);
  const [submitting,         setSubmitting]         = useState(false);
  const [collectionPoint,    setCollectionPoint]    = useState<CollectionPoint | null>(null);
  const [customerAddresses,  setCustomerAddresses]  = useState<Address[] | null>(null);
  const [newDelivery,        setNewDelivery]        = useState<DeliveryFormData>({ driver_id: null, destination_id: null });

  const [logsLoaded, setLogsLoaded] = useState(false);

  const isCollectionOrder = currentOrder?.delivery_type === 'collection';
  const isChatType        = currentOrder?.type === 'chat';
  const currentStatus     = (currentOrder?.status ?? '') as OrderStatus;

  const isArchived = ARCHIVED_ORDER_STATUSES.includes(currentStatus);

  const showPrintButton              = canShowPrintButton(currentStatus, isChatType);
  const showAssignPackerButton       = canAssignPacker(currentStatus);
  const showUnassignPickerButton     = canUnassignPicker(currentStatus);
  const showDeliveryButton           = canShowDeliveryButton(currentStatus, isCollectionOrder, isChatType);
  const showCompleteCollectionButton = canShowCompleteCollectionButton(currentStatus, isCollectionOrder, isChatType);
  const showFulfillmentActions       = canShowFulfillmentActions(currentStatus, isChatType, isArchived);

  const availablePickers = useMemo(() => {
    const storeId = currentOrder?.store_id;
    if (!storeId) return [];
    return pickers.filter(p => p.store_id === storeId);
  }, [pickers, currentOrder?.store_id]);

  useEffect(() => {
    const valid = availablePickers.some(p => String(p.id) === String(selectedPickerId));
    if (!valid) setSelectedPickerId('');
  }, [availablePickers, selectedPickerId]);

  const load = useCallback(async (orderId: string, initialTab?: TabType) => {
    const order = await fetchOrder(orderId);
    if (!order) { error('Failed to load order data'); return; }

    if (initialTab === 'deliveries') {
      void fetchDeliveries(orderId).then(() => setDeliveriesLoaded(true));
    }

    if (order.collection_point_id) {
      try {
        const points = await fetchCollectionPointsFromDb();
        setCollectionPoint(points.find(p => p.id === order.collection_point_id) ?? null);
      } catch { setCollectionPoint(null); }
    } else {
      setCollectionPoint(null);
    }

    const addressId = order.delivery_address_id || order.customer?.delivery_address;
    if (addressId) {
      try {
        const addr = await fetchAddressByIdFromDb(String(addressId));
        if (addr) {
          setCustomerAddresses([addr]);
          setNewDelivery(prev => ({ ...prev, destination_id: addr.id }));
        }
      } catch { setCustomerAddresses(null); }
    } else {
      setCustomerAddresses(null);
    }
  }, [fetchOrder, fetchDeliveries, error]);

  const locationState       = location.state as { activeTab?: TabType } | null;
  const initialTabFromState = locationState?.activeTab;

  useEffect(() => {
    if (!id) return;
    setPaymentsLoaded(false);
    setDeliveriesLoaded(false);
    setLogsLoaded(false);
    if (initialTabFromState) {
      setActiveTab(initialTabFromState);
      navigate(location.pathname, { replace: true, state: {} });
    }
    void load(id, initialTabFromState);
  }, [id, load, initialTabFromState, navigate, location.pathname]);

  useEffect(() => {
    // Payments now also feed the Overview tab's "Refunded to Card" line, so
    // fetch on Overview too, not just when the Payments tab itself is opened.
    if (!id || paymentsLoaded) return;
    if (activeTab !== 'payments' && activeTab !== 'overview') return;
    void fetchPayments(id).then(() => setPaymentsLoaded(true));
  }, [id, activeTab, paymentsLoaded, fetchPayments]);

  useEffect(() => {
    if (!id || deliveriesLoaded) return;
    if (activeTab === 'deliveries') {
      void fetchDeliveries(id).then(() => setDeliveriesLoaded(true));
    }
  }, [id, activeTab, deliveriesLoaded, fetchDeliveries]);

  useEffect(() => {
    if (!id || logsLoaded) return;
    if (activeTab === 'logs') {
      void fetchTimeline(id).then(() => setLogsLoaded(true));
    }
  }, [id, activeTab, logsLoaded, fetchTimeline]);

  useEffect(() => {
    if (!showPrintConfirm || pickers.length > 0) return;
    void fetchPickers();
  }, [showPrintConfirm, pickers.length, fetchPickers]);

  // Keep the latest activeTab/fetchTimeline available inside the realtime
  // callback below without making the channel effect depend on them —
  // otherwise every tab switch would tear down and resubscribe the
  // Supabase channel. Without this, the callback would silently keep
  // using whatever activeTab was at the moment the channel first
  // subscribed (a stale-closure bug), not the tab actually active when
  // an event fires.
  const activeTabRef = useRef(activeTab);
  const fetchTimelineRef = useRef(fetchTimeline);
  useEffect(() => {
    activeTabRef.current = activeTab;
    fetchTimelineRef.current = fetchTimeline;
  }, [activeTab, fetchTimeline]);

  useEffect(() => {
    if (!id) return;
    const channel = supabase
        .channel(`order-details-${id}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'orders', filter: `id=eq.${id}` }, () => { void load(id); })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'order_status_history', filter: `order_id=eq.${id}` }, () => {
          void load(id);
          if (activeTabRef.current === 'logs') {
            void fetchTimelineRef.current(id);
          } else {
            setLogsLoaded(false);
          }
        })
        .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [id, load]);

  useEffect(() => {
    const state = location.state as { activeTab?: TabType } | null;
    if (state?.activeTab) {
      setActiveTab(state.activeTab);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const confirmPackerAssignment = async () => {
    if (!id || !currentOrder?.id || !selectedPickerId) { error('Please select a picker.'); return; }
    setSubmitting(true);
    try {
      const isReassignment = currentOrder.status === ORDER_STATUS.PACKER_ASSIGNED;
      await updateOrderStatus(String(currentOrder.id), ORDER_STATUS.PACKER_ASSIGNED, { model_id: selectedPickerId, model_type: 'Picker' });
      await load(id);
      success(isReassignment ? 'Picker updated successfully.' : 'Picker assigned and order moved to Packing.');
      setShowPrintConfirm(false);
      setSelectedPickerId('');
    } catch { error('Failed to assign picker.'); }
    finally { setSubmitting(false); }
  };

  const confirmUnassignPicker = async () => {
    if (!id || !currentOrder?.id) return;
    setSubmitting(true);
    try {
      await unassignPickerFromOrderInDb(String(currentOrder.id));
      await load(id);
      success('Picker unassigned. Order returned to packing queue.');
      setShowUnassignPickerConfirm(false);
    } catch (err) {
      error(err instanceof Error ? err.message : 'Failed to unassign picker.');
    } finally { setSubmitting(false); }
  };

  const confirmCompletePacking = async () => {
    if (!currentOrder || !id) return;
    setSubmitting(true);
    try {
      await updateOrderStatus(String(currentOrder.id), ORDER_STATUS.PACKING_COMPLETE);
      if (isCollectionOrder) {
        await updateOrderStatus(String(currentOrder.id), ORDER_STATUS.READY_FOR_COLLECTION);
        success('Packing complete. Customer notified to collect.');
      } else {
        await updateOrderStatus(String(currentOrder.id), ORDER_STATUS.ASSIGN_DRIVER);
        success('Packing complete. Order moved to Driver Assignment.');
      }
      await load(id);
      setShowPackingConfirm(false);
    } catch { error('An error occurred during status transition.'); }
    finally { setSubmitting(false); }
  };

  const confirmCompleteCollection = async () => {
    if (!currentOrder || !id) return;
    if (enteredPin !== currentOrder.pin_code) { setPinError('Incorrect PIN. Please ask the customer to check their app.'); return; }
    setSubmitting(true);
    try {
      await updateOrderStatus(String(currentOrder.id), ORDER_STATUS.COLLECTION_COMPLETE);
      await load(id);
      setShowPinModal(false);
      setEnteredPin('');
      success('Collection complete. Order finalised.');
    } catch { error('Failed to complete collection.'); }
    finally { setSubmitting(false); }
  };

  const createDeliveryOrShowModal = () => {
    if (currentOrder?.deliveries?.some(d => d.status === ORDER_STATUS.DELIVERY_COMPLETE)) {
      error('This order has already been delivered.'); return;
    }
    if (currentOrder?.deliveries?.some(d => ([ORDER_STATUS.DRIVER_ASSIGNED, ORDER_STATUS.ON_ROUTE] as string[]).includes(d.status))) {
      error('There is already an active delivery in progress.'); return;
    }
    setIsModalOpen(true);
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewDelivery(prev => ({ ...prev, [name]: value === '' ? null : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDelivery.driver_id || !newDelivery.destination_id || !id || !currentOrder) return;
    setSubmitting(true);
    try {
      const success_flag = await assignDriverToOrderInDb(
          id,
          String(currentOrder.customer_id),
          String(newDelivery.destination_id),
          String(newDelivery.driver_id)
      );
      if (success_flag) {
        setIsModalOpen(false);
        setNewDelivery(prev => ({ ...prev, driver_id: null }));
        await load(id);
        success('Driver assigned successfully.');
      }
    } catch { error('Failed to assign driver'); }
    finally { setSubmitting(false); }
  };

  if (!id) return <div className="p-8 text-stone-600">Invalid order ID.</div>;

  return (
      <div className="min-h-screen bg-stone-50 print:py-2">
        <div className="mx-auto space-y-6 print:px-0">
          <div className="flex items-center gap-3 print:hidden">
            <Link to="/orders" className="text-sm text-stone-600 hover:text-stone-800 hover:underline">
              ← Back to orders
            </Link>
          </div>

          {currentOrder === null ? (
              <div className="animate-pulse space-y-6">
                <div className="h-28 rounded-2xl bg-white border border-stone-200 shadow-sm p-6" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="rounded-2xl bg-white border border-stone-200 shadow-sm p-6 h-40" />
                  <div className="rounded-2xl bg-white border border-stone-200 shadow-sm p-6 lg:col-span-2 h-40" />
                </div>
              </div>
          ) : (
              <div className="rounded-2xl bg-white border border-stone-200 shadow-sm print:border-0 print:shadow-none">
                <div className="border-b border-stone-200 print:hidden">
                  <div className="flex items-center justify-between gap-4 px-6 pt-6">
                    <nav className="flex gap-8" aria-label="Tabs">
                      {(['overview', 'details', 'payments', 'logs', 'deliveries'] as TabType[]).map(tab => (
                          <button key={tab} onClick={() => setActiveTab(tab)}
                                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                                      activeTab === tab
                                          ? 'border-[#ee2b74] text-[#ee2b74]'
                                          : 'border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300'
                                  }`}>
                            {tab === 'overview'   && 'Order Overview'}
                            {tab === 'details'    && 'Order Details'}
                            {tab === 'payments'   && 'Payments'}
                            {tab === 'logs'       && 'Status & Logs'}
                            {tab === 'deliveries' && 'Delivery Tracking'}
                          </button>
                      ))}
                    </nav>

                    <div className="flex gap-x-2">
                      {showFulfillmentActions ? (
                          <>
                            {showPrintButton && (
                                <button onClick={() => { setActiveTab('details'); setTimeout(() => window.print(), 0); }}
                                        className="print:hidden flex items-center rounded-md px-3 py-3 text-sm font-semibold text-white bg-[#ee2b74] hover:bg-[#d4245f]">
                                  Print Order
                                </button>
                            )}
                            {showAssignPackerButton && (
                                <button onClick={() => setShowPrintConfirm(true)} disabled={submitting}
                                        className="print:hidden flex items-center rounded-md px-3 py-3 text-sm font-semibold text-white bg-stone-900 hover:bg-stone-800 disabled:opacity-50">
                                  {submitting ? 'Processing…' : 'Assign Packer'}
                                </button>
                            )}
                            {showUnassignPickerButton && (
                                <button onClick={() => setShowUnassignPickerConfirm(true)} disabled={submitting}
                                        className="print:hidden flex items-center rounded-md px-3 py-3 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 disabled:opacity-50">
                                  {submitting ? 'Processing…' : 'Unassign Picker'}
                                </button>
                            )}

                            {showDeliveryButton && (
                                <button onClick={createDeliveryOrShowModal} disabled={submitting}
                                        className="print:hidden inline-flex items-center rounded-md px-3 py-3 text-sm font-semibold text-white bg-stone-900 hover:bg-stone-800">
                                  {submitting ? 'Creating…' : 'Create Delivery'}
                                </button>
                            )}
                            {showCompleteCollectionButton && (
                                <button onClick={() => { setEnteredPin(''); setPinError(''); setShowPinModal(true); }} disabled={submitting}
                                        className="print:hidden inline-flex items-center rounded-md px-3 py-3 text-sm font-semibold text-white bg-[#ee2b74] hover:bg-[#d4245f]">
                                  {submitting ? 'Saving…' : getCompleteCollectionLabel(currentStatus)}
                                </button>
                            )}
                          </>
                      ) : (
                          <span className="text-xs font-medium text-stone-400 italic bg-stone-100 px-3 py-1 rounded-full">
                                            {getPaymentStageLabel(currentStatus, isArchived)}
                                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-6 print:p-0">
                  {activeTab === 'overview' && (
                      <OrderOverviewTab
                          currentOrder={currentOrder}
                          onOrderUpdate={() => load(id)}
                          orderItems={orderItems}
                          customerAddresses={customerAddresses}
                          walletTransactions={walletTransactions}
                          collectionPoint={collectionPoint}
                          cakeOrder={cakeOrder}
                          payments={payments as Payment[]}
                      />
                  )}
                  {activeTab === 'details' && (
                      <>
                        <div className="print:mb-2 mb-6 flex items-center justify-between">
                          <h2 className="text-lg font-semibold text-stone-900">
                            {isChatType ? 'WhatsApp Order' : 'Line Items'}
                          </h2>
                        </div>
                        <OrderDetailsTab currentOrder={currentOrder} items={orderItems as OrderItem[]} itemHistory={itemHistory} cakeOrder={cakeOrder} onRefresh={() => load(id)} />
                      </>
                  )}
                  {activeTab === 'payments'   && <PaymentsTab payments={payments as Payment[]} paymentHistory={paymentHistory} />}
                  {activeTab === 'deliveries' && (
                      <DeliveryDetailsTab
                          deliveries={(currentOrder.deliveries ?? [])}
                          orderAddress={customerAddresses?.[0]}
                          orderPin={currentOrder.pin_code ?? null}
                      />
                  )}
                  {activeTab === 'logs' && <StatusesLogsTab orderId={id} timeline={timeline} loading={timelineLoading} />}
                </div>
              </div>
          )}
        </div>

        {/* Unassign picker confirmation */}
        <ConfirmModal isOpen={showUnassignPickerConfirm} title="Unassign Picker"
                      message="Are you sure you want to unassign the current picker? The order will be returned to the packing queue."
                      onConfirm={confirmUnassignPicker} onCancel={() => setShowUnassignPickerConfirm(false)} loading={submitting}>
          <div className="flex items-center gap-3 text-stone-600">
            <svg className="h-5 w-5 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-xs font-medium">This cannot be undone if the picker has already started scanning items. A picking session in progress will block this action.</p>
          </div>
        </ConfirmModal>

        {/* Packing confirmation */}
        <ConfirmModal isOpen={showPackingConfirm} title="Confirm Packing Completion"
                      message={isCollectionOrder
                          ? 'Are you sure all items are packed and ready for collection? The customer will be notified to collect.'
                          : 'Are you sure all items are packed and ready for delivery? This will move the order to driver assignment.'}
                      onConfirm={confirmCompletePacking} onCancel={() => setShowPackingConfirm(false)} loading={submitting}>
          <div className="text-sm text-stone-600">
            <p className="font-semibold mb-2">Before confirming:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Verify all line items match the physical stock</li>
              {isCollectionOrder && <li>Stage the packed items at the correct collection point</li>}
            </ul>
          </div>
        </ConfirmModal>

        {/* Picker assignment */}
        <ConfirmModal isOpen={showPrintConfirm} title="Assign Picker & Confirm Print"
                      message="Ensure the order printed correctly. You must select a picker to continue."
                      onConfirm={confirmPackerAssignment} onCancel={() => setShowPrintConfirm(false)}
                      loading={submitting} confirmDisabled={!selectedPickerId || availablePickers.length === 0}>
          <div className="space-y-2">
            <label htmlFor="picker-select" className="block text-sm font-medium text-stone-700">Assign to Picker</label>
            <div className="relative w-full">
              <select id="picker-select" value={selectedPickerId} onChange={e => setSelectedPickerId(e.target.value)}
                      disabled={availablePickers.length === 0}
                      className="block w-full appearance-none rounded-lg border border-stone-300 bg-white py-2 pl-3 pr-8 text-sm text-stone-700 shadow-sm outline-none focus:ring-2 focus:ring-[#ee2b74] disabled:bg-stone-100">
                <option value="">{availablePickers.length === 0 ? 'No matching pickers available' : 'Select a picker…'}</option>
                {availablePickers.map(p => (
                    <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-stone-400">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7" /></svg>
              </div>
            </div>
            {availablePickers.length === 0 && <p className="mt-2 text-xs text-amber-600">No pickers are assigned to this order's store.</p>}
            {availablePickers.length > 0 && !selectedPickerId && <p className="mt-2 text-xs text-red-600">Please select a picker before confirming.</p>}
          </div>
        </ConfirmModal>

        {/* Collection PIN */}
        <ConfirmModal isOpen={showPinModal} title="Verify Collection PIN"
                      message="Ask the customer for their 4-digit PIN and enter it below to confirm handover."
                      onConfirm={confirmCompleteCollection}
                      onCancel={() => { setShowPinModal(false); setEnteredPin(''); setPinError(''); }}
                      loading={submitting} confirmDisabled={enteredPin.length !== 4}>
          <div className="space-y-3">
            <input type="text" inputMode="numeric" maxLength={4} value={enteredPin}
                   onChange={e => { setPinError(''); setEnteredPin(e.target.value.replace(/\D/g, '')); }}
                   placeholder="Enter 4-digit PIN"
                   className="block w-full rounded-lg border border-stone-300 px-4 py-3 text-center text-lg font-bold tracking-[0.5em] text-stone-900 focus:border-[#ee2b74] focus:ring-2 focus:ring-[#ee2b74] outline-none"
                   autoFocus />
            {pinError && <p className="text-sm text-red-600 text-center">{pinError}</p>}
          </div>
        </ConfirmModal>

        <CreateDeliveryModal
            isOpen={isModalOpen}
            onClose={() => { setIsModalOpen(false); setNewDelivery(prev => ({ ...prev, driver_id: null })); }}
            onSubmit={handleSubmit}
            onDeliveryChange={handleFieldChange}
            onDriverChange={handleFieldChange}
            newDelivery={newDelivery}
            loading={submitting || loading}
            customerAddresses={customerAddresses}
            selectedOrder={currentOrder}
        />
      </div>
  );
};

export default OrderDetails;